import Immutable from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { PageId } from 'records/Page';
import { PublishRequest, PublishRequestMap } from 'records/PublishRequest';
import { Discipline } from 'records/Discipline';
import { PublishRequestDiscipline } from 'records/PublishRequestDiscipline';

export interface PublishRequestActions {
  generatePublishRequests: (actionParams: AppStateActionParams) => AppState;

  setPublishRequests: (
    actionParams: AppStateActionParams,
    nextPublishRequests: PublishRequestMap
  ) => AppState;

  updatePublishRequest: (
    actionParams: AppStateActionParams,
    updatedPR: PublishRequest
  ) => AppState;

  setSelectedPublishRequests: (
    actionParams: AppStateActionParams,
    updatedSelectedPubReqs: Immutable.Set<PageId>
  ) => AppState;

  deleteSelectedPublishReqs: (actionParams: AppStateActionParams) => AppState;

  clearSelectedPublishRequests: (actions: AppStateActionParams) => AppState;
}

const publishRequestActions: PublishRequestActions = {
  generatePublishRequests: ({ state }: AppStateActionParams): AppState => {
    const {
      pagesReadyForReview,
      settings: { sheets: sheetSettings },
    } = state();
    const pubReqs = pagesReadyForReview
      .sort((a, b) => {
        if (a.filename < b.filename) {
          return -1;
        }
        if (a.filename > b.filename) {
          return 1;
        }
        if (a.pageIndex < b.pageIndex) {
          return -1;
        }
        return 1;
      })
      .map((prfr) => {
        const designator = prfr.inferredDisciplineDesignator;
        const sheetDiscipline = sheetSettings.disciplines.get(designator, null);

        return new PublishRequest({
          pageid: prfr.pageid,
          documentid: prfr.documentid,
          issueddate: Math.ceil(Date.now() / 1000),
          sheettitle: prfr.inferredsheettitle,
          sheetnumber: prfr.inferredsheetnumber,
          discipline: designator,
          sheetDiscipline: sheetDiscipline
            ? new PublishRequestDiscipline({
                id: sheetDiscipline.id,
                designator: sheetDiscipline.designator,
                description: sheetDiscipline.description,
              })
            : null,
        });
      });

    return state().set('publishRequests', pubReqs);
  },

  setPublishRequests: (
    { state }: AppStateActionParams,
    nextPublishRequests: PublishRequestMap
  ): AppState => {
    const {
      settings: { sheets: sheetSettings },
    } = state();

    const publishRequests = nextPublishRequests.map((publishRequest) => {
      const sheetDiscipline = sheetSettings.disciplines.get(
        publishRequest.get('discipline', ''),
        null
      );

      return publishRequest.set(
        'sheetDiscipline',
        sheetDiscipline
          ? new PublishRequestDiscipline({
              id: sheetDiscipline.id,
              designator: sheetDiscipline.designator,
              description: sheetDiscipline.description,
            })
          : null
      );
    });

    return state().set('publishRequests', publishRequests);
  },

  setSelectedPublishRequests: (
    { state }: AppStateActionParams,
    updatedSelectedPubReqs: Immutable.Set<PageId>
  ): AppState => {
    return state().set('selectedPublishRequests', updatedSelectedPubReqs);
  },

  updatePublishRequest: (
    { state }: AppStateActionParams,
    updatedPublishRequest: PublishRequest
  ): AppState => {
    const {
      settings: { sheets: sheetSettings },
    } = state();

    const sheetDiscipline = sheetSettings.disciplines.get(
      updatedPublishRequest.get('discipline', ''),
      null
    );
    return state().setIn(
      ['publishRequests', updatedPublishRequest.pageid],
      updatedPublishRequest.set(
        'sheetDiscipline',
        sheetDiscipline
          ? new PublishRequestDiscipline({
              id: sheetDiscipline.id,
              designator: sheetDiscipline.designator,
              description: sheetDiscipline.description,
            })
          : null
      )
    );
  },

  deleteSelectedPublishReqs: ({ state }: AppStateActionParams): AppState => {
    const { publishRequests, selectedPublishRequests } = state();

    const updatedPRs = selectedPublishRequests.reduce(
      (acc, id) => acc.delete(id),
      publishRequests
    );
    return state()
      .set('publishRequests', updatedPRs)
      .set('selectedPublishRequests', Immutable.Set());
  },

  clearSelectedPublishRequests: ({ state }: AppStateActionParams): AppState => {
    return state().set('selectedPublishRequests', Immutable.Set());
  },
};

export default publishRequestActions;
